'use client';

import type { CSSProperties } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import ButtonV2 from 'components/shared/ButtonV2';

import { scienceLandingAsset } from './asset';
import styles from './HeroLanding.module.css';

type HeroLandingProps = { bannerPx: number };

export default function HeroLanding({ bannerPx }: HeroLandingProps) {
  const t = useTranslations('science');
  return (
    <section
      className={styles.frame}
      style={{ '--banner-px': `${bannerPx}px` } as CSSProperties}
      aria-labelledby="science-hero-title"
    >
      <div className={styles.card}>
        {/* the video's own first frame, under it, so the hero never shows a
            different image or an empty card while the video loads — sized per
            device by next/image and cropped exactly like the video */}
        <Image
          className={styles.bg}
          src={scienceLandingAsset('hero-poster.jpg')}
          alt=""
          aria-hidden="true"
          fill
          sizes="100vw"
          priority
        />
        <video className={styles.bg} aria-hidden="true" autoPlay muted loop playsInline preload="metadata">
          {/* Small encode only for genuinely low-res screens. Phones get the 4K
              master; ?v= skips a year-cached SpeedSize rendition of the old file. */}
          <source
            src={scienceLandingAsset('hero-mobile.mp4')}
            media="(max-width: 480px) and (max-resolution: 1.5dppx)"
            type="video/mp4"
          />
          <source src={`${scienceLandingAsset('hero.mp4')}?v=20261013`} type="video/mp4" />
        </video>
        <div className={styles.leftScrim} aria-hidden="true" />
        <div className={styles.content}>
          <h1 id="science-hero-title" className={styles.title}>
            {t('science.landing.hero.title')}
          </h1>
          {/* desktop adds a second line ("Explore our…"); phones keep the first sentence */}
          <p className={styles.lede}>
            {t('science.landing.hero.lede')}
            <span className={styles.ledeLong}>
              <br />
              {t('science.landing.hero.ledeMore')}
            </span>
          </p>
          <div className={styles.ctas}>
            <ButtonV2
              href="/science/studies/"
              variant="white"
              className={styles.cta}
              label={t('science.landing.hero.browseStudies')}
            />
            <ButtonV2
              href="/science/explainers/"
              variant="glassLight"
              className={styles.cta}
              label={t('science.landing.hero.readExplainers')}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
